import React, { useEffect, useReducer, useState, useContext } from "react";

import {
  Button,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { Box, Card, CardContent, Grid } from "@mui/material";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { DeleteOutline, Edit } from "@material-ui/icons";
import QueueModal from "../../components/QueueModal";
import { toast } from "react-toastify";
import ConfirmationModal from "../../components/ConfirmationModal";
// import { SocketContext } from "../../context/Socket/SocketContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import ForbiddenPage from "../../components/ForbiddenPage";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(0),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
    border: "none",
  },
  customTableCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_QUEUES") {
    const queues = action.payload;
    const newState = [...state];

    queues.forEach((queue) => {
      const queueIndex = newState.findIndex((q) => q.id === queue.id);
      if (queueIndex !== -1) {
        newState[queueIndex] = queue;
      } else {
        newState.push(queue);
      }
    });

    return newState;
  }

  if (action.type === "UPDATE_QUEUES") {
    const queue = action.payload;
    const queueIndex = state.findIndex((u) => u.id === queue.id);

    if (queueIndex !== -1) {
      const newState = [...state];
      newState[queueIndex] = queue;
      return newState;
    } else {
      return [queue, ...state];
    }
  }

  if (action.type === "DELETE_QUEUE") {
    const queueId = action.payload;
    const queueIndex = state.findIndex((q) => q.id === queueId);
    if (queueIndex !== -1) {
      const newState = [...state];
      newState.splice(queueIndex, 1);
      return newState;
    }
    return state;
  }

  if (action.type === "RESET") {
    return [];
  }
};

const Queues = () => {
  const classes = useStyles();

  const [queues, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);

  const [queueModalOpen, setQueueModalOpen] = useState(false);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  //   const socketManager = useContext(SocketContext);
  const { user, socket } = useContext(AuthContext);
  const companyId = user.companyId;

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/queue");
        dispatch({ type: "LOAD_QUEUES", payload: data });

        setLoading(false);
      } catch (err) {
        toastError(err);
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const onQueueEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_QUEUES", payload: data.queue });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_QUEUE", payload: data.queueId });
      }
    };
    socket.on(`company-${companyId}-queue`, onQueueEvent);

    return () => {
      socket.off(`company-${companyId}-queue`, onQueueEvent);
    };
  }, [socket, companyId]);

  const handleOpenQueueModal = () => {
    setQueueModalOpen(true);
    setSelectedQueue(null);
  };

  const handleCloseQueueModal = () => {
    setQueueModalOpen(false);
    setSelectedQueue(null);
  };

  const handleEditQueue = (queue) => {
    setSelectedQueue(queue);
    setQueueModalOpen(true);
  };

  const handleCloseConfirmationModal = () => {
    setConfirmModalOpen(false);
    setSelectedQueue(null);
  };

  const handleDeleteQueue = async (queueId) => {
    try {
      await api.delete(`/queue/${queueId}`);
      toast.success(i18n.t("Queue deleted successfully!"));
    } catch (err) {
      toastError(err);
    }
    setSelectedQueue(null);
  };

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 3, width: "100%" }}
    >
      <ConfirmationModal
        title={
          selectedQueue &&
          `${i18n.t("queues.confirmationModal.deleteTitle")} ${
            selectedQueue.name
          }?`
        }
        open={confirmModalOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={() => handleDeleteQueue(selectedQueue.id)}
      >
        {i18n.t("queues.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <QueueModal
        open={queueModalOpen}
        onClose={handleCloseQueueModal}
        queueId={selectedQueue?.id}
        onEdit={(res) => {
          if (res) {
            setTimeout(() => {
              handleEditQueue(res);
            }, 500);
          }
        }}
      />
      {user.profile === "user" ? (
        <ForbiddenPage />
      ) : (
        <>
          <Card
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
            }}
          >
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Box>
                  <Typography
                    variant="h6"
                    color="primary"
                    gutterBottom
                    fontWeight="bold"
                  >
                    Roteamento e Setores de Atendimento
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Crie filas, direcione contatos e defina cores de
                    organização.
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleOpenQueueModal}
                >
                  {i18n.t("queues.buttons.add")}
                </Button>
              </Box>
              <Paper
                className={classes.mainPaper}
                elevation={0}
                variant="outlined"
                style={{ backgroundColor: "transparent", border: "none", padding: "16px" }}
              >
                <Grid container spacing={2}>
                  {queues.map((queue) => (
                    <Grid item xs={12} sm={6} md={4} key={queue.id}>
                      <Card elevation={0} sx={{ borderRadius: 2, height: "100%", border: "1px solid", borderColor: "divider" }}>
                        <CardContent>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <span
                                style={{
                                  backgroundColor: queue.color,
                                  width: 20,
                                  height: 20,
                                  borderRadius: "50%",
                                  display: "inline-block"
                                }}
                              />
                              <Typography variant="h6" color="primary" fontWeight="bold">
                                {queue.name}
                              </Typography>
                            </Box>
                            <Box display="flex">
                              <IconButton size="small" onClick={() => handleEditQueue(queue)}>
                                <Edit color="secondary" />
                              </IconButton>
                              <IconButton size="small" onClick={() => {
                                setSelectedQueue(queue);
                                setConfirmModalOpen(true);
                              }}>
                                <DeleteOutline color="error" />
                              </IconButton>
                            </Box>
                          </Box>
                          <Typography variant="caption" color="textSecondary" display="block">
                            {i18n.t("queues.table.greeting")}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 2, height: 40, overflow: "hidden", textOverflow: "ellipsis" }}>
                            {queue.greetingMessage || '-'}
                          </Typography>
                          
                          <Box display="flex" justifyContent="space-between">
                            <Box>
                              <Typography variant="caption" color="textSecondary" display="block">
                                {i18n.t("queues.table.ID")}
                              </Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {queue.id}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="textSecondary" display="block">
                                {i18n.t("queues.table.orderQueue")}
                              </Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {queue.orderQueue || '-'}
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                  {loading && (
                    <Grid item xs={12}>
                      <TableRowSkeleton columns={4} />
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default Queues;
